import React from "react"
import { useTranslation } from "react-i18next"

import { relativeTime } from "../helpers/relative-time.ts"
import { EntityIcon } from "../map/EntityIcon.tsx"
import type { AnalyticsRecentActivityEvent } from "../redux/global-map.types"

interface RecentActivityCardProps {
  events: AnalyticsRecentActivityEvent[]
  onSelectEvent: (event: AnalyticsRecentActivityEvent) => void
  onViewAll: () => void
}

/**
 * Spec §11: five most recent permitted events, newest first (already
 * deduplicated server-side — `analytics.py::_recent_activity`). Selecting
 * an event opens the related record — the same `selectFeature`-shaped
 * navigation every other Global Map row click already uses.
 */
export function RecentActivityCard({
  events,
  onSelectEvent,
  onViewAll
}: RecentActivityCardProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          {t("globalMap.analytics.recentActivity.title")}
        </span>
        <button
          type="button"
          onClick={onViewAll}
          className="text-xs font-medium text-blue-600 hover:underline"
        >
          {t("globalMap.analytics.recentActivity.viewAll")}
        </button>
      </div>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {events.map((event, index) => (
          <li key={`${event.recordId}-${event.timestamp}-${index}`}>
            <button
              type="button"
              onClick={() => onSelectEvent(event)}
              className="flex w-full min-w-0 items-start gap-2 rounded px-1 py-1.5 text-left hover:bg-gray-50"
            >
              <EntityIcon
                entityType={event.entityType}
                className="mt-0.5 size-5 shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-gray-900">
                  {event.description}
                </div>
                <div className="truncate text-xs text-slate-500">
                  {event.recordTitle}
                </div>
              </div>
              <span className="shrink-0 whitespace-nowrap text-[11px] text-slate-400">
                {relativeTime(event.timestamp)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
